<template>
  <section class="recommended-section">
    <!-- Section Header -->
    <div class="section-header">
      <h2 class="section-title">ແບບປະກັນທີ່ຍອດນິຍົມ</h2>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="skeleton-grid">
        <div v-for="n in 3" :key="n" class="skeleton-card">
          <div class="skeleton-image"></div>
          <div class="skeleton-content">
            <div class="skeleton-line short"></div>
            <div class="skeleton-line medium"></div>
            <div class="skeleton-line long"></div>
            <div class="skeleton-btn"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <p>ບໍ່ສາມາດໂຫລດຂໍ້ມູນໄດ້ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ</p>
    </div>

    <!-- Cards Grid -->
    <div v-else class="cards-grid">
      <ProductCard
        v-for="(product, index) in recommendedProducts"
        :key="product.id"
        :product="product"
        :animation-delay="`${index * 0.12}s`"
        @click="handleProductClick"
      />
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import ProductCard from "@/components/Product/ProductCard.vue";
import {
  loadLifeInsuranceProducts,
  getLifeProductById,
} from "@/data/lifeInsurance";

const router = useRouter();

// IDs ที่ต้องการแสดง (ตามลำดับ)
const RECOMMENDED_IDS = ["whole-life-01", "ordinary-20-15", "ordinary-12-10"];

const recommendedProducts = ref([]);
const isLoading = ref(true);
const error = ref(false);

onMounted(async () => {
  try {
    isLoading.value = true;
    await loadLifeInsuranceProducts();

    // ดึงเฉพาะ 3 product ที่ต้องการ ตามลำดับ
    const products = RECOMMENDED_IDS.map((id) => getLifeProductById(id)).filter(
      Boolean
    );

    recommendedProducts.value = products;
  } catch (err) {
    console.error("Failed to load recommended products:", err);
    error.value = true;
  } finally {
    isLoading.value = false;
  }
});

const handleProductClick = (product) => {
  router.push({
    name: "life-insurance-detail",
    params: { slug: product.slug },
  });
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Noto+Sans+Lao:wght@300;400;500;600;700;800&display=swap");

.recommended-section {
  font-family: "Noto Sans Lao", sans-serif;
  padding: 4rem 2rem 5rem;
  width: 100%;
  box-sizing: border-box;
}

/* ─── Section Header ─────────────────────────────── */
.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-title {
  font-size: clamp(1.6rem, 3.5vw, 2.2rem);
  font-weight: 800;
  color: #344873;
  margin: 0 0 0.75rem;
  letter-spacing: -0.01em;
}

/* ─── Cards Grid ─────────────────────────────────── */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem;
  max-width: 72rem;
  margin: 0 auto;
}

/* ─── Loading Skeleton ───────────────────────────── */
.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.75rem;
  max-width: 72rem;
  margin: 0 auto;
}

.skeleton-card {
  background: #fff;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.06);
}

.skeleton-image {
  height: 12rem;
  background: linear-gradient(90deg, #e8eaed 25%, #f4f5f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.skeleton-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-line {
  height: 0.875rem;
  border-radius: 0.5rem;
  background: linear-gradient(90deg, #e8eaed 25%, #f4f5f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.skeleton-line.short {
  width: 40%;
}
.skeleton-line.medium {
  width: 65%;
}
.skeleton-line.long {
  width: 85%;
}

.skeleton-btn {
  height: 2.75rem;
  border-radius: 99px;
  margin-top: 0.5rem;
  background: linear-gradient(90deg, #e8eaed 25%, #f4f5f7 50%, #e8eaed 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ─── Error State ────────────────────────────────── */
.error-state {
  text-align: center;
  color: #6b7280;
  padding: 3rem;
  font-size: 1rem;
}

/* ─── Responsive ─────────────────────────────────── */
@media (max-width: 64rem) {
  .cards-grid,
  .skeleton-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 40rem) {
  .recommended-section {
    padding: 2.5rem 1rem 3rem;
  }

  .cards-grid,
  .skeleton-grid {
    grid-template-columns: 1fr;
    max-width: 28rem;
  }

  .section-title {
    font-size: 1.4rem;
  }
}
</style>